import { cn } from '../lib/cn';

/**
 * Police du logotype.
 *
 * Aucune police n'est chargée par l'application : la pile ne peut contenir que
 * ce qui est déjà installé. Ces géométriques-là — Avenir chez Apple, Century
 * Gothic chez Microsoft — sont celles d'un logo de cabinet, et se distinguent
 * du texte du message, qui reste en police système.
 */
const POLICE_LOGO =
  "'Avenir Next', Avenir, Futura, 'Century Gothic', 'Trebuchet MS', system-ui, sans-serif";

/**
 * Coupe le nom à sa deuxième majuscule : « SkDental » → « Sk » + « Dental ».
 *
 * C'est la coupure qu'un logotype à deux couleurs demande. Un nom qui ne s'y
 * prête pas — « Cabinet du Lac » — reste d'un seul tenant plutôt que d'être
 * coupé n'importe où.
 */
function couperNom(nom: string): [string, string] {
  const coupure = nom.slice(1).search(/[A-Z]/);
  return coupure === -1 ? [nom, ''] : [nom.slice(0, coupure + 1), nom.slice(coupure + 1)];
}

/**
 * Logo du cabinet, en attendant qu'il puisse en téléverser un.
 *
 * Le nom vient de l'appelant — celui du cabinet connecté — et non d'une
 * constante : c'est la ligne qui identifie *ce* cabinet en tête de signature.
 *
 * Un logotype et non une image : aucun logo n'est stocké nulle part
 * aujourd'hui, et afficher un fichier d'exemple aurait laissé croire que
 * celui-ci partirait vraiment. Le nom composé, lui, tient le rôle — la ligne
 * qui identifie le cabinet en tête de signature — et reste juste si aucun
 * fichier n'est jamais téléversé.
 *
 * Deux couleurs et deux graisses sur la coupure du nom, comme le fait tout
 * logotype de ce genre. Les teintes sont passées par l'appelant : le bleu nuit
 * de la marque s'éteindrait sur un Gmail sombre.
 */
export function LogoCabinet({
  nom,
  url,
  marque,
  accent,
  className,
}: {
  nom: string;
  /** Logo déposé par le cabinet. Présent, c'est lui qui s'affiche. */
  url?: string | null;
  marque: string;
  accent: string;
  className?: string;
}) {
  const [tete, queue] = couperNom(nom);

  // L'image quand le cabinet en a déposé une : c'est exactement ce que le
  // patient recevra, et le logotype dessiné ne sert plus qu'à ceux qui n'en
  // ont pas.
  if (url) {
    return (
      <img
        src={url}
        alt={nom}
        className={cn('mb-3 block max-h-12 w-auto max-w-[200px] object-contain', className)}
      />
    );
  }

  return (
    <span
      aria-hidden="true"
      style={{ fontFamily: POLICE_LOGO }}
      className={cn('mb-3 flex w-fit text-[17px] leading-none tracking-[-0.01em]', className)}
    >
      <span style={{ color: marque }} className="font-semibold">
        {tete}
      </span>
      <span style={{ color: accent }} className="font-medium">
        {queue}
      </span>
    </span>
  );
}
